import { axiosInstance } from "@/config/axios-wrapper";

export interface EventDetails {
  title: string;
  date: string; // ISO 8601 format
  end_date?: string; // ISO 8601 format
  location: string;
  max_participants?: number;
  description?: string;
}

export interface CreatePostPayload {
  content: string;
  visibility: "public" | "private";
  hashtag_names: string[];
  tagged_users: string[];
  media_urls?: string[];
  is_event?: boolean;
  event_details?: EventDetails;
}

export interface Hashtag {
  name: string;
  id: string;
}

export interface TaggedUser {
  full_name: string;
  photo_thumbnail_url: string;
  id: string;
  type?: "employer" | "job_seeker";
}

export interface Author {
  id: string;
  full_name: string;
  email: string;
  type: "employer" | "job_seeker";
  photo: string;
  photo_thumbnail_url: string;
  job_title: string;
  organization_id?: string;
}

export interface EditHistory {
  edited_at: string;
  previous_content: string;
  edited_by: string;
}

export interface Like {
  userId: string;
  userName: string;
  emoji: string;
}

export interface ReactionStat {
  emoji: string;
  count: number;
}

export interface UserReaction {
  user_id: string;
  target_type: string;
  target_id: string;
  emoji: string;
  created_at: string;
  updated_at: string;
  id: string;
}

export interface Post {
  id: string;
  author_id?: string;
  author_type: "employer" | "job_seeker";
  type: "post" | "shared_post" | "event" | "shared" | "regular";
  content: string;
  media_urls: string[];
  "media_urls:raw"?: string[]; // Raw S3 keys for editing
  tagged_users: string[] | TaggedUser[];
  hashtags: string[] | Hashtag[];
  visibility: "public" | "private";
  likes_count: number;
  comments_count: number;
  shares_count: number;
  views_count: number;
  deleted: boolean;
  deleted_at?: string;
  edit_history: EditHistory[];
  created_at: string;
  updated_at: string;
  __v: number;
  author?: Author;
  // Shared post specific fields
  shared_post_id?: string;
  share_content?: string;
  shared_post?: Post;
  original_post?: Post;
  // Event specific fields
  is_event?: boolean;
  event_details?: EventDetails;
  event_status?: "active" | "cancelled";
  event_participants?: Array<{
    user: any;
    user_id: string;
    status: string;
    registered_at: string;
  }>;
  // Reaction fields (inline from API)
  reaction?: UserReaction | null; // Current user's reaction (null if not reacted)
  reaction_stats?: ReactionStat[]; // Aggregated reaction counts by emoji
  // Report/Flag fields
  is_reported?: boolean;
  is_flagged?: boolean;
  // Optional fields for local state (overridden by PostData)
  likes?: Like[];
  comments?: any[];
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface CreatePostResponse {
  success: boolean;
  result: {
    post: Post;
  };
}

interface GetPostsResponse {
  success: boolean;
  result: {
    posts: Post[];
    pagination: Pagination;
  };
}

export interface GetPostsParams {
  page?: number;
  limit?: number;
  sort?: string;
  order?: "asc" | "desc";
}

export interface GetEventsParams {
  page?: number;
  limit?: number;
  sort?: string;
  order?: "asc" | "desc";
}

export interface UpdatePostPayload {
  content: string;
  visibility?: "public" | "private";
  hashtag_names?: string[];
  media_urls?: string[];
  tagged_users?: string[];
  event_status?: "active" | "cancelled";
  event_details?: EventDetails;
}

interface UpdatePostResponse {
  success: boolean;
  result: Post;
}

export const createPost = async (payload: CreatePostPayload) => {
  const response = await axiosInstance.post<CreatePostResponse>("/posts", payload);
  return response.data.result.post;
};

export const getPosts = async (params: GetPostsParams = {}) => {
  const { page = 1, limit = 20, sort = "created_at", order = "desc" } = params;
  const response = await axiosInstance.get<GetPostsResponse>("/posts", {
    params: { page, limit, sort, order, visibility: "public" },
  });
  return response.data.result;
};

export const getUserEvents = async (userId: string, params: GetEventsParams = {}) => {
  const { page = 1, limit = 20, order = "desc", sort = "created_at" } = params;
  const response = await axiosInstance.get<GetPostsResponse>(`/events/user/${userId}/created`, {
    params: { page, limit, order, sort },
  });
  return response.data.result;
};

export const getUserAttendingEvents = async (userId: string, params: GetEventsParams = {}) => {
  const { page = 1, limit = 20, sort = "date", order = "desc" } = params;
  const response = await axiosInstance.get<GetPostsResponse>(`/events/user/${userId}/attending`, {
    params: { page, limit, sort, order },
  });
  return response.data.result;
};

export const getRecommendedEvents = async (params: GetEventsParams = {}) => {
  const { page = 1, limit = 20, order = "asc" } = params;
  const response = await axiosInstance.get<GetPostsResponse>("/events", {
    params: { page, limit, order },
  });
  return response.data.result;
};

export const getUserPosts = async (userId: string, params: GetPostsParams = {}) => {
  const { page = 1, limit = 20, sort = "created_at", order = "desc" } = params;
  const response = await axiosInstance.get<GetPostsResponse>(`/posts/user/${userId}`, {
    params: { page, limit, sort, order, visibility: "public" },
  });
  return response.data.result;
};

export const updatePost = async (postId: string, payload: UpdatePostPayload) => {
  const response = await axiosInstance.put<UpdatePostResponse>(`/posts/${postId}`, payload);
  return response.data.result;
};

export interface DeletePostPayload {
  reason: string;
}

export const deletePost = async (postId: string, payload: DeletePostPayload) => {
  const response = await axiosInstance.delete(`/posts/${postId}`);
  return response.data;
};

export interface LikePostPayload {
  emoji: string;
}

export const reactPost = async (postId: string, payload: LikePostPayload) => {
  const response = await axiosInstance.post(`/posts/${postId}/reactions`, payload);
  return response.data;
};

export const removeReactPost = async (postId: string) => {
  const response = await axiosInstance.delete(`/posts/${postId}/reactions`);
  return response.data;
};

export interface ReactionUser {
  target_type: string;
  target_id: string;
  emoji: string;
  created_at: string;
  updated_at: string;
  user: {
    full_name: string;
    type: "employer" | "job_seeker";
    photo_thumbnail_url: string;
    job_title: string;
    id: string;
    organization_id?: string;
  };
  id: string;
}

export interface GetReactionsResponse {
  success: boolean;
  result: {
    reactions: ReactionUser[];
    pagination: Pagination;
  };
}

export const getPostReactions = async (postId: string, page = 1, limit = 50) => {
  const response = await axiosInstance.get<GetReactionsResponse>(`/posts/${postId}/reactions`, {
    params: { page, limit },
  });
  return response.data.result;
};

// Comment Reactions
export interface CommentReactionPayload {
  emoji: string;
}

export const reactComment = async (commentId: string, payload: CommentReactionPayload) => {
  const response = await axiosInstance.post(`/comments/${commentId}/reactions`, payload);
  return response.data;
};

export const removeReactComment = async (commentId: string) => {
  const response = await axiosInstance.delete(`/comments/${commentId}/reactions`);
  return response.data;
};

export const getCommentReactions = async (commentId: string, page = 1, limit = 50) => {
  const response = await axiosInstance.get<GetReactionsResponse>(`/comments/${commentId}/reactions`, {
    params: { page, limit },
  });
  return response.data.result;
};

export interface CreateCommentPayload {
  post_id: string;
  content: string;
  mentioned_users?: string[];
  parent_comment_id?: string;
}

export interface Comment {
  _id: string;
  id?: string;
  post_id: string;
  author_id: string;
  author_type: "employer" | "job_seeker";
  content: string;
  mentioned_users: string[];
  parent_comment_id?: string;
  likes_count: number;
  replies_count: number;
  deleted: boolean;
  created_at: string;
  updated_at: string;
  author?: Author;
  replies?: Comment[];
}

interface GetCommentsResponse {
  success: boolean;
  result: {
    comments: Comment[];
    pagination: Pagination;
  };
}

interface GetRepliesResponse {
  success: boolean;
  result: {
    replies: Comment[];
    pagination: Pagination;
  };
}

interface GetNestedCommentsResponse {
  success: boolean;
  result: {
    comments: Comment[];
  };
}

interface CreateCommentResponse {
  success: boolean;
  result: Comment;
}

export const createComment = async (payload: CreateCommentPayload) => {
  const response = await axiosInstance.post<CreateCommentResponse>(`/comments`, payload);
  return response.data.result;
};

export const getCommentsByPost = async (postId: string, page = 1, limit = 50) => {
  const response = await axiosInstance.get<GetCommentsResponse>(`/comments/post/${postId}`, {
    params: { page, limit },
  });
  return response.data.result;
};

export const getNestedCommentsByPost = async (postId: string) => {
  const response = await axiosInstance.get<GetNestedCommentsResponse>(`/comments/post/${postId}/nested`);
  return response.data.result.comments;
};

export const getRepliesByComment = async (commentId: string, page = 1, limit = 50) => {
  const response = await axiosInstance.get<GetRepliesResponse>(`/comments/${commentId}/replies`, {
    params: { page, limit },
  });
  return response.data.result;
};

export interface CreateReplyPayload {
  content: string;
  mentioned_users?: string[];
}

export const createReply = async (commentId: string, payload: CreateReplyPayload) => {
  const response = await axiosInstance.post(`/comments/${commentId}/replies`, payload);
  return response.data;
};

export interface UpdateCommentPayload {
  content: string;
  mentioned_users?: string[];
}

export const updateComment = async (commentId: string, payload: UpdateCommentPayload) => {
  const response = await axiosInstance.put(`/comments/${commentId}`, payload);
  return response.data;
};

export const deleteComment = async (commentId: string, reason = "Deleted by user") => {
  const response = await axiosInstance.delete(`/comments/${commentId}`, {
    data: { reason },
  });
  return response.data;
};

export const getUserComments = async (userId: string, page = 1, limit = 20) => {
  const response = await axiosInstance.get<GetCommentsResponse>(`/comments/user/${userId}`, {
    params: { page, limit },
  });
  return response.data.result;
};

export interface SharePostPayload {
  share_content?: string;
}

interface SharePostResponse {
  success: boolean;
  result: {
    post: Post;
  };
}

export const sharePost = async (postId: string, payload: SharePostPayload = {}) => {
  const response = await axiosInstance.post<SharePostResponse>(`/posts/${postId}/share`, {
    visibility: "public",
  });
  return response.data.result.post;
};

// Share response - the API returns the shared posts themselves
export interface ShareUser {
  author: {
    id: string;
    full_name: string;
    email: string;
    type: "employer" | "job_seeker";
    photo_thumbnail_url: string;
    job_title: string;
    organization_id?: string;
  };
  created_at: string; // This is when the share happened
  id: string;
  type: "shared";
}

interface GetPostSharesResponse {
  success: boolean;
  result: {
    posts: ShareUser[]; // API returns "posts" not "shares"
    pagination: Pagination;
  };
}

export const getPostShares = async (postId: string, page = 1, limit = 50) => {
  const response = await axiosInstance.get<GetPostSharesResponse>(`/posts/${postId}/shares`, {
    params: { page, limit },
  });
  // Return posts as shares with transformed structure for easier use
  return {
    shares: response.data.result.posts,
    pagination: response.data.result.pagination,
  };
};

// Event API Functions
interface EventRegistrationResponse {
  success: boolean;
  message: string;
}

export const registerEvent = async (eventId: string) => {
  const response = await axiosInstance.post<EventRegistrationResponse>(`/events/${eventId}/register`);
  return response.data;
};

export const unregisterEvent = async (eventId: string) => {
  const response = await axiosInstance.delete<EventRegistrationResponse>(`/events/${eventId}/register`);
  return response.data;
};

export const cancelEvent = async (eventId: string) => {
  const response = await axiosInstance.post<EventRegistrationResponse>(`/events/${eventId}/cancel`);
  return response.data;
};

// Resubmit flagged event for review
export interface ResubmitEventPayload {
  event_details: {
    title: string;
    date: string; // ISO 8601 format
    end_date?: string; // ISO 8601 format
    location: string;
    description?: string;
    max_participants?: number;
  };
}

interface ResubmitEventResponse {
  success: boolean;
  message: string;
}

export const resubmitEvent = async (postId: string, payload: ResubmitEventPayload) => {
  const response = await axiosInstance.post<ResubmitEventResponse>(`/posts/${postId}/resubmit`, payload);
  return response.data;
};

// Cancel flagged event
export interface CancelFlaggedEventPayload {
  reason: string;
}

interface CancelFlaggedEventResponse {
  success: boolean;
  message: string;
}

export const cancelFlaggedEvent = async (postId: string, payload: CancelFlaggedEventPayload) => {
  const response = await axiosInstance.post<CancelFlaggedEventResponse>(`/posts/${postId}/cancel-flagged`, payload);
  return response.data;
};

export interface SearchUser {
  id: string;
  full_name: string;
  email: string;
  photo_thumbnail_url: string;
  score: number;
  type?: "employer" | "job_seeker";
  organization_id?: string;
}

interface SearchUsersResponse {
  success: boolean;
  result: SearchUser[];
}

export const searchUsers = async (query: string) => {
  const response = await axiosInstance<SearchUsersResponse>("/users/search", {
    params: { query },
  });
  return response.data.result;
};

// Get post by ID
interface GetPostByIdResponse {
  success: boolean;
  result: Post;
}

export const getPostById = async (postId: string) => {
  const response = await axiosInstance.get<GetPostByIdResponse>(`/posts/${postId}`);
  return response.data.result;
};

// Get event by ID
interface GetEventByIdResponse {
  success: boolean;
  result: Post;
}

export const getEventById = async (eventId: string) => {
  const response = await axiosInstance.get<GetEventByIdResponse>(`/events/${eventId}`);
  return response.data.result;
};

// Report API
export type ReportTargetType = 'post' | 'comment';

export interface ReportPayload {
  reason: string;
  description?: string;
  media_urls?: string[];
}

interface ReportApiPayload {
  target_type: ReportTargetType;
  target_id: string;
  reason: string;
  description?: string;
  media_urls?: string[];
}

interface ReportResponse {
  success: boolean;
  message: string;
}

export const reportContent = async (
  targetType: ReportTargetType,
  targetId: string,
  payload: ReportPayload
) => {
  const apiPayload: ReportApiPayload = {
    target_type: targetType,
    target_id: targetId,
    reason: payload.reason,
    description: payload.description,
    media_urls: payload.media_urls,
  };

  const response = await axiosInstance.post<ReportResponse>('/reports', apiPayload);
  return response.data;
};

// Get My Reports (reports I've made)
export interface Report {
  id: string;
  target_type: ReportTargetType;
  content_type?: 'post' | 'comment' | 'reply' | 'event';
  target_id: string;
  reason: string;
  description?: string;
  media_urls?: string[];
  status: 'pending' | 'actioned' | 'resolved' | 'dismissed';
  action_taken?: 'keep' | 'remove_warning' | 'suspend' | 'ban' | 'hold';
  admin_comment?: string;
  reviewed_at?: string;
  admin?: {
    id: string;
    email: string;
    full_name: string;
  } | null;
  created_at: string;
  updated_at: string;
  target?: {
    id: string;
    content?: string;
    title?: string;
    author?: {
      id: string;
      full_name: string;
      photo?: string;
    };
  };
}

export interface ReportsStats {
  posts: number;
  comments: number;
  replies: number;
  events: number;
  total: number;
}

interface GetMyReportsResponse {
  success: boolean;
  result: {
    reports: Report[];
    stats: ReportsStats;
    pagination: {
      page: number;
      limit: number;
      total: number;
      total_pages: number;
    };
  };
}

// Reports on my content - different structure from "my reports"
export interface OwnContentReportEventDetails {
  title: string;
  date: string;
  end_date?: string;
  location: string;
  description?: string;
  max_participants?: number;
  recurrence?: string;
  status?: 'active' | 'cancelled';
}

export interface OwnContentReportEventParticipants {
  going_count: number;
  interested_count: number;
  not_going_count: number;
  total_registered: number;
}

export interface OwnContentReportReporter {
  id: string;
  full_name: string;
  email: string;
  photo_thumbnail_url?: string;
}

export interface OwnContentReportActionSummary {
  action: 'flagged' | 'warning' | 'removed' | 'suspended' | 'banned' | 'hold';
  message: string;
  flagged_at?: string;
  time_remaining_hours?: number;
  admin_note?: string;
}

export interface OwnContentReportEventMetadata {
  status?: 'waiting_action' | 'resubmitted' | 'approved' | 'rejected' | 'event_cancelled';
  resubmission_count?: number;
  remaining_attempts?: number;
  max_attempts?: number;
  last_resubmission_date?: string;
  has_new_action?: boolean;
}

export interface OwnContentReport {
  target_type: ReportTargetType;
  target_id: string;
  reason: string;
  priority: 'low' | 'medium' | 'high';
  status: 'pending' | 'actioned' | 'dismissed' | 'resolved';
  action_taken?: 'keep' | 'remove_warning' | 'suspend' | 'ban' | 'hold';
  admin_comment?: string;
  created_at: string;
  updated_at: string;
  reviewed_at?: string;
  media_urls?: string[];
  'media_urls:raw'?: string[];
  reporter_name: string;
  reporter_id: string;
  content_title?: string | null;
  content_preview?: string | null;
  action_summary?: OwnContentReportActionSummary | null;
  event_details?: OwnContentReportEventDetails;
  event_participants?: OwnContentReportEventParticipants;
  event_metadata?: OwnContentReportEventMetadata;
  reporter: OwnContentReportReporter;
}

interface GetReportsOnMyContentResponse {
  success: boolean;
  result: {
    reports: OwnContentReport[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

export const getMyReports = async (page: number = 1, limit: number = 20) => {
  const response = await axiosInstance.get<GetMyReportsResponse>('/reports/my', {
    params: { page, limit },
  });
  return response.data.result;
};

export const getReportsOnMyContent = async (page: number = 1, limit: number = 20) => {
  const response = await axiosInstance.get<GetReportsOnMyContentResponse>('/reports/on-my-content', {
    params: { page, limit },
  });
  return response.data.result;
};

// Get my reports stats for sidebar notification count
export interface MyReportsStatsResponse {
  success: boolean;
  result: {
    total: number;
    by_content_type: {
      posts: number;
      comments: number;
      replies: number;
      events: number;
    };
    by_status: {
      pending: number;
      actioned: number;
    };
  };
}

export const getMyReportsStats = async () => {
  const response = await axiosInstance.get<MyReportsStatsResponse>('/reports/my/stats');
  return response.data.result;
};

// Get reports on my content stats
export interface OwnContentReportsStatsResponse {
  success: boolean;
  result: {
    total: number;
    by_content_type: {
      posts: number;
      comments: number;
      replies: number;
      events: number;
    };
    by_status: {
      waiting_action: number;
      resubmitted: number;
      approved: number;
      rejected: number;
    };
  };
}

export const getReportsOnMyContentStats = async () => {
  const response = await axiosInstance.get<OwnContentReportsStatsResponse>('/reports/on-my-content/stats');
  return response.data.result;
};
